import { useState } from "react";
import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { Card, CardContent } from "@/shared/components/card";
import { Button } from "@/shared/components/button";
import { usePatientProfile } from "@/portals/patient/context/PatientProfileContext";
import type { InsuranceSummary } from "@/features/patient/dashboard/utils/adaptInsurance";

interface InsuranceCardsSectionProps {
  coverage?: InsuranceSummary;
  isLoading: boolean;
}

function formatDate(dateString: string): string {
  if (!dateString) return "—";
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "—";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function formatExpiry(dateString?: string): string {
  if (!dateString) return "—";
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "—";
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yy = String(date.getFullYear()).slice(-2);
  return `${mm}/${yy}`;
}

export function InsuranceCardsSection({ coverage, isLoading }: InsuranceCardsSectionProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const { profile } = usePatientProfile();
  const subscriberName = `${profile.firstName} ${profile.lastName}`.trim();

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const planYear = coverage?.startDate ? new Date(coverage.startDate + "T00:00:00").getFullYear() : null;

  return (
    <div className="p-6 lg:p-8">
      {/* Summary Stats */}
      <div className="mb-5 grid gap-5 sm:grid-cols-3">
        {[
          { label: "Active Plans", value: coverage ? "1" : "0", icon: "lucide:shield" },
          { label: "Digital Card", value: coverage?.isActive ? "Active" : "—", icon: "lucide:credit-card" },
          { label: "Plan Year", value: planYear ? String(planYear) : "—", icon: "lucide:calendar" },
        ].map(({ label, value, icon }) => (
          <div
            key={label}
            className="relative flex min-h-[110px] flex-col justify-end rounded-3xl border border-[#E5E7EB] bg-white px-6 py-5 shadow-sm"
          >
            <div className="absolute right-5 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-[#E4002B]/50">
              <Icon icon={icon} className="h-4 w-4 !text-white" />
            </div>
            <p className="text-2xl font-extrabold text-[#0A2947]">{isLoading ? "…" : value}</p>
            <p className="mt-1 text-xs font-semibold text-[#6B7280]">{label}</p>
          </div>
        ))}
      </div>

      {coverage ? (
        <div className="overflow-hidden rounded-3xl border border-[#E5E7EB] bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-[#E5E7EB] bg-white px-5 py-4">
            <div className="flex items-center gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0A2947]">
                <Icon icon="lucide:shield" className="h-5 w-5 !text-white" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-lg font-extrabold tracking-tight text-[#0A2947]">
                    {coverage.name}
                  </h2>
                  {coverage.isActive && (
                    <span className="rounded-md bg-[#0A2947] px-2 py-0.5 text-[11px] font-semibold !text-white">
                      Active
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs font-medium text-[#6B7280]">{coverage.planType}</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => setIsExpanded((expanded) => !expanded)}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? "Collapse insurance card details" : "Expand insurance card details"}
              className="!flex !h-9 !w-9 !items-center !justify-center !rounded-full !border-[#E5E7EB] !p-0 !text-[#9CA3AF] hover:!bg-[#F3F4F6]"
            >
              <Icon
                icon="lucide:chevron-down"
                className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
              />
            </Button>
          </div>

          {isExpanded && <div className="grid gap-5 px-8 py-5 lg:grid-cols-2">
            <div className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-4">
              <h3 className="mb-4 text-sm font-extrabold text-[#0A2947]">Plan Details</h3>
              {[
                { label: "Member ID", value: coverage.memberId },
                { label: "Group Number", value: coverage.groupNumber || "—" },
                { label: "Start Date", value: formatDate(coverage.startDate) },
              ].map((item) => (
                <div key={item.label} className="mb-4 flex items-end justify-between gap-3 last:mb-0">
                  <div>
                    <p className="text-xs font-extrabold text-[#0A2947]">{item.label}</p>
                    <p className="mt-1 text-xs font-medium text-[#9CA3AF]">{item.value}</p>
                  </div>
                  <Button
                    variant="link"
                    onClick={() => copyToClipboard(item.value, `coverage-${item.label}`)}
                    className="!h-auto !p-0 !text-[#9CA3AF] hover:!text-[#0A2947]"
                  >
                    {copiedField === `coverage-${item.label}` ? (
                      <Icon icon="lucide:check-circle-2" className="h-4 w-4" />
                    ) : (
                      <Icon icon="lucide:copy" className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>

            <div
              className="relative flex flex-col justify-between overflow-hidden rounded-2xl bg-[#0A1E42] p-5 text-white"
              style={{
                backgroundImage:
                  "repeating-radial-gradient(circle at 50% 120%, transparent 0, transparent 10px, rgba(255,255,255,0.08) 11px, rgba(255,255,255,0.08) 12px)",
                backgroundSize: "36px 36px",
              }}
            >
              <div>
                <p className="text-xs font-semibold text-white/60">Member #{coverage.memberId}</p>
                {coverage.groupNumber && (
                  <p className="text-xs font-semibold text-white/60">Group #{coverage.groupNumber}</p>
                )}
              </div>
              <div className="mt-8">
                <p className="text-base font-extrabold leading-tight">{subscriberName}</p>
                <p className="mt-2 text-xs text-white/60">
                  Exp <span className="font-bold text-white">{formatExpiry(coverage.endDate)}</span>
                </p>
              </div>
            </div>
          </div>}
        </div>
      ) : (
        <Card className="rounded-3xl border-[#E5E7EB] bg-white shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Icon icon="lucide:shield" className="h-12 w-12 text-[#9CA3AF]" />
            {isLoading ? (
              <Skeleton active title={{ width: 160 }} paragraph={false} className="mt-4" />
            ) : (
              <p className="mt-4 text-lg font-extrabold text-[#0A2947]">No insurance information</p>
            )}
            {!isLoading && (
              <p className="mt-1 text-sm text-[#6B7280]">
                Upload records that include your insurance coverage to see it here.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
